// Venue accessibility summary, ported from the app so both show the same thing:
//   PiattoRNMobileApp screens/business/BusinessScreen.tsx buildAccessibilityCategories()
//   components/venue/VenueAccessibilitySummary.tsx (narrative, categories, confidence)
//   constants/confidence.ts, types/venueAccessibility.ts (labels)
// Keep in step with those files.

export const CONFIDENCE_LABELS = {
  5: { label: "High confidence", short: "High" },
  4: { label: "Good confidence", short: "Good" },
  3: { label: "Moderate confidence", short: "Moderate" },
  2: { label: "Low confidence", short: "Low" },
  1: { label: "Unknown", short: "Unknown" },
};

export const STATUS_LABELS = {
  available: "Available",
  not_available: "Not available",
  partial: "Partial",
  unknown: "Unknown",
};

const yesNo = (v) => (v ? "available" : "not_available");

const SOURCE_LABELS = { piatto_staff: "Checked by Piatto staff" };

const toDate = (v) => {
  if (!v) return null;
  if (typeof v.toDate === "function") return v.toDate();
  if (typeof v.seconds === "number") return new Date(v.seconds * 1000);
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Structured step-free entry (newFunctions, 2026-11-03): bizStepFreeEntry
 * true/false, absent = unknown. Set by Piatto staff; never inferred from
 * Google's wheelchair flags, which are shown separately.
 */
export function stepFreeEntryFeature(venue) {
  const v = venue?.bizStepFreeEntry;
  if (v !== true && v !== false) return null;
  const when = toDate(venue?.bizStepFreeEntryUpdatedAt);
  const source = SOURCE_LABELS[venue?.bizStepFreeEntrySource] || null;
  const details = [
    source,
    when ? when.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : null,
  ].filter(Boolean).join(", ");
  return {
    label: v ? "Step-free entry" : "No step-free entry",
    status: yesNo(v),
    informational: true, // label already says it; avoid "No step-free entry: Not available"
    ...(details ? { details } : {}),
  };
}

export function buildAccessibilityCategories(venue) {
  const acc = venue?.bizAccessibility;

  const physical = [];
  const stepFree = stepFreeEntryFeature(venue);
  if (stepFree) physical.push(stepFree);
  if (acc?.wheelchairAccessibleEntrance != null) {
    physical.push({ label: "Wheelchair accessible entrance", status: yesNo(acc.wheelchairAccessibleEntrance) });
  }
  if (acc?.wheelchairAccessibleParking != null) {
    physical.push({ label: "Accessible parking", status: yesNo(acc.wheelchairAccessibleParking) });
  }
  if (acc?.wheelchairAccessibleRestroom != null) {
    physical.push({ label: "Accessible bathroom", status: yesNo(acc.wheelchairAccessibleRestroom) });
  }
  if (venue?.bizEntry) {
    // A description, not a yes/no: shown as text (the app shows it with a tick).
    physical.push({ label: `Entry: ${venue.bizEntry}`, status: "available", informational: true });
  }

  const service = [];
  if (venue?.bizServesVegetarianFood) service.push({ label: "Vegetarian options", status: "available" });
  if (venue?.bizMenuForChildren) service.push({ label: "Children's menu", status: "available" });

  const seating = [];
  if (venue?.bizOutdoorSeating != null) {
    seating.push({ label: "Outdoor seating", status: yesNo(venue.bizOutdoorSeating) });
  }

  let physicalConfidence = physical.length > 0 ? 3 : 1;
  // Google-sourced accessibility data → moderate-to-good confidence (as the app does)
  if (acc) physicalConfidence = 4;
  // Staff-checked step-free entry → at least Good
  if (stepFree) physicalConfidence = Math.max(physicalConfidence, 4);

  return [
    { key: "physical", label: "Physical Access", confidence: physicalConfidence, features: physical },
    { key: "sensory", label: "Sensory Environment", confidence: 1, features: [] },
    { key: "communication", label: "Communication", confidence: 1, features: [] },
    { key: "service", label: "Service", confidence: service.length > 0 ? 3 : 1, features: service },
    { key: "seating", label: "Seating", confidence: seating.length > 0 ? 3 : 1, features: seating },
  ];
}

/** App: bizAccessRating clamped to 1–5, else 1 ("Unknown"). */
export const overallConfidence = (venue) => {
  const n = Math.round(Number(venue?.bizAccessRating) || 0);
  return n >= 1 ? Math.min(n, 5) : 1;
};

/** Venue-entered details from the web venue editor (not shown in the app). */
export const hasVenueEnteredAccessibility = (venue) =>
  !!(
    (venue?.bizDisBathType && venue.bizDisBathType !== "none") ||
    venue?.bizDisEntrance ||
    venue?.bizDisBathDir ||
    venue?.bizDisStdBathDir
  );
